import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import { X } from "lucide-react";

export default function Blog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [params, setParams] = useSearchParams();
    const activeTag = params.get("tag") || "";

    useEffect(() => {
        api.get("/blog").then((r) => setPosts(r.data)).finally(() => setLoading(false));
    }, []);

    const filtered = activeTag
        ? posts.filter((p) => {
            const tags = Array.isArray(p.tags) ? p.tags : [];
            return (p.category === activeTag) || tags.includes(activeTag);
        })
        : posts;

    return (
        <div className="bg-cream-light pt-28" data-testid="blog-page">
            <SEO title="Media & Blog" description="Stories steeped in steam, sugar, and stillness." path="/blog" />
            <section className="px-6 md:px-12 py-16 md:py-24">
                <div className="max-w-[1480px] mx-auto">
                    <Reveal>
                        <div className="tracked text-coffee/50 text-[10px] mb-6">Media & Blog</div>
                        <h1
                            className="font-editorial font-bold text-coffee leading-[0.95] max-w-4xl"
                            style={{ fontSize: "clamp(2.6rem, 6.5vw, 6rem)" }}
                        >
                            Stories steeped in steam, sugar, and stillness.
                        </h1>
                    </Reveal>

                    {activeTag && (
                        <div className="mt-8 inline-flex items-center gap-3 px-4 py-2.5 border border-gold text-coffee bg-gold/10" data-testid="active-tag-filter">
                            <span className="tracked text-[10px]">Filtered by: <span className="text-gold">{activeTag}</span></span>
                            <button onClick={() => setParams({})} className="text-coffee/60 hover:text-coffee" aria-label="clear filter" data-testid="clear-tag-filter">
                                <X size={12} />
                            </button>
                        </div>
                    )}

                    {loading ? (
                        <div className="mt-20 tracked text-coffee/50">Loading…</div>
                    ) : filtered.length === 0 ? (
                        <div className="mt-20 font-editorial font-bold text-coffee/60 text-2xl">
                            {activeTag ? `No stories tagged "${activeTag}" yet.` : "Stories are brewing. Come back soon."}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mt-16">
                            {filtered.map((p, i) => (
                                <Reveal key={p.slug} delay={i * 0.08}>
                                    <Link to={`/blog/${p.slug}`} className="group block" data-testid={`blog-card-${p.slug}`}>
                                        <div className="aspect-[4/5] overflow-hidden bg-coffee/5 mb-5">
                                            <img
                                                src={fileUrl(p.cover_image)}
                                                alt={p.title}
                                                loading="lazy"
                                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                                            />
                                        </div>
                                        <div className="font-mono tracked text-coffee/40 text-[9px] mb-2">{p.category}</div>
                                        <h2 className="font-editorial font-bold text-2xl text-coffee gold-underline inline-block">
                                            {p.title}
                                        </h2>
                                        <p className="font-body text-coffee/70 mt-3 leading-relaxed text-sm">
                                            {p.excerpt}
                                        </p>
                                    </Link>
                                </Reveal>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
